import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { CompanyData, getCompanyData, useModulesConfig } from '~/data/company';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import { useSWR } from '~/hooks/useSWR';
import { employeeModulesAccessRoles } from '~/scenes/PortalViews/Views/EmployeeViews/data';
import { useNewEmployeeViewAccess } from '~/scenes/PortalViews/Views/EmployeeViews/NewEmployeeView/useNewEmployeeViewAccess';
import { AppModules, hasOneOfRoles } from '~/services/Permissions';
import { getENVs } from '~/utils/helper';

export interface UseTopbarOutput {
  employeeId: number;
  loggedInEmployeeId: number;
  canAdminister: boolean;
  canAddEmployee: boolean;
  canAddEmployeeCandidate: boolean;
  logoId: string | undefined;
  companyData?: CompanyData;
  companyDataLoading: boolean;
  companyDataFailedToLoad: boolean;
  newEmployeePage: string;
  isAIChatbotEnabled: boolean;
}

export const useTopbar = (): UseTopbarOutput => {
  const { isModuleEnabled } = useModulesConfig();

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const {
    selectedEmployeeId,
    isSelectedEmployeeLoggedInUser,
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();
  const userRoles = useLoggedInUserRoles();

  const hasAccessToEmployeeModule =
    hasRoleAccessOverEmployee(employeeModulesAccessRoles.people.all) ||
    isSelectedEmployeeLoggedInUser;

  const canAdminister = hasOneOfRoles(userRoles, [
    Roles.HR_ADMIN,
    Roles.HR_ADMIN_LIMITED,
    Roles.HR_STAFF,
    Roles.HR_DEV,
  ]);

  const isAIChatbotEnabled =
    isModuleEnabled(AppModules.AI_CHATBOT) && canAdminister;

  const { canAddEmployee, canAddEmployeeCandidate } =
    useNewEmployeeViewAccess();

  const {
    data: companyData,
    loading: companyDataLoading,
    failedToLoad: companyDataFailedToLoad,
  } = useSWR('getCompanyData', getCompanyData);

  const getLogoId = () => {
    if (!companyData || !companyData.logo.fComLogoId) {
      return 'dfltLogo';
    }

    return companyData.logo.fComLogoId;
  };

  const logoId = getLogoId();
  const enableEmploymentTemplates = getENVs(
    'REACT_APP_ENABLE_EMPLOYMENT_TEMPLATES',
  );
  const newEmployeePage =
    enableEmploymentTemplates === 'yes'
      ? 'page-newEmpFormTemplate.html'
      : 'page-newEmp.html';

  return {
    companyData,
    employeeId: hasAccessToEmployeeModule
      ? selectedEmployeeId
      : loggedInEmployeeId,
    loggedInEmployeeId,
    canAdminister,
    canAddEmployee,
    canAddEmployeeCandidate,
    logoId,
    companyDataLoading,
    companyDataFailedToLoad,
    newEmployeePage,
    isAIChatbotEnabled,
  };
};
